import { type RefObject, useCallback, useEffect, useRef } from 'react';

interface DelayedIntersectionObserverProps {
    onIntersecting?: () => void;
    delay?: number;
    rootMargin?: string;
    threshold?: number;
}

export const useDelayedIntersectionObserver = ({
    onIntersecting,
    delay = 0,
    rootMargin = '',
    threshold = 0.1,
}: DelayedIntersectionObserverProps): RefObject<HTMLDivElement> => {
    const elementRef = useRef<HTMLDivElement>(null);

    const callbackRef = useRef(onIntersecting);
    useEffect(() => {
        callbackRef.current = onIntersecting;
    }, [onIntersecting]);

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const timeoutRef = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        const el = elementRef.current;
        if (!el) {
            return;
        }

        clearTimer();

        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry) {
                    return;
                }

                if (entry.isIntersecting) {
                    timeoutRef.current = window.setTimeout(() => {
                        timeoutRef.current = null;
                        if (isMountedRef.current) {
                            callbackRef.current?.();
                        }
                    }, delay);
                } else {
                    clearTimer();
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(el);

        return () => {
            clearTimer();
            observer.disconnect();
        };
    }, [delay, rootMargin, threshold, clearTimer]);

    return elementRef;
};
